/**
 * ResidentManagementPanel — S6 Phase 3. Shared by the PM portal and NECS
 * Admin. Units with residents + linked users, per-resident status control,
 * onboarding emails, unit hold/release, open occupancy flags, QR invites.
 */
import { useCallback, useEffect, useState } from 'react';
import { Users, RefreshCw, Mail, Flag, QrCode, PauseCircle, PlayCircle } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';
import { toast } from 'sonner';
import QrInvitePoster from './QrInvitePoster';
import { roleTermFor, accountLabelFor } from '../lib/roleTerm';

const GOLD = '#C9A962';
const STATUS_STYLES = {
  active: 'bg-emerald-950/50 text-emerald-400',
  pending: 'bg-blue-950/50 text-blue-400',
  on_hold: 'bg-amber-950/50 text-amber-400',
  deactivated: 'bg-zinc-800 text-zinc-500',
};

export default function ResidentManagementPanel({ propertyId, propertyName, propertyType, isAdmin = false }) {
  const occupant = roleTermFor(propertyType);
  const [data, setData] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try { setData(await apiFetch(`/api/resident/manage/overview?property_id=${propertyId}`)); }
    catch (e) { setData({ units: {}, flags: [], active_code: null }); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (userId, status) => {
    if (status === 'deactivated' && !window.confirm('Deactivate this account? Personal data is archived and the unit slot opens for a new registration. This cannot be undone.')) return;
    try {
      await apiFetch(`/api/resident/manage/residents/${userId}/status`, { method: 'PATCH', body: { status } });
      toast.success(`Status set to ${status.replace('_', ' ')}`);
      load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  const unitStatus = async (unit, status) => {
    if (status === 'deactivated' && !window.confirm(`Deactivate ALL accounts on unit ${unit}? Data is archived; the unit opens for new registrations.`)) return;
    try {
      const res = await apiFetch('/api/resident/manage/units/status', { method: 'POST', body: { property_id: propertyId, unit_number: unit, status } });
      toast.success(`${res.affected} account(s) → ${status.replace('_', ' ')}`);
      load();
    } catch (e) { toast.error(e.detail || 'Update failed'); }
  };

  const sendOnboarding = async (userId) => {
    try {
      const res = await apiFetch(`/api/resident/manage/residents/${userId}/send-onboarding`, { method: 'POST' });
      toast.success(res.email_sent ? 'Onboarding email sent' : 'Account approved (email delivery pending)');
      load();
    } catch (e) { toast.error(e.detail || 'Send failed'); }
  };

  const resolveFlag = async (flagId) => {
    try {
      const res = await apiFetch(`/api/resident/manage/flags/${flagId}`, { method: 'PATCH', body: { reactivate_unit: true } });
      toast.success(`Flag resolved · ${res.released} account(s) reactivated`);
      load();
    } catch (e) { toast.error(e.detail || 'Resolve failed'); }
  };

  const resetRecord = async (r) => {
    if (!window.confirm(`Lease renewal for ${r.name}? All streaks and counts reset to zero — earned badges are archived as historical records.`)) return;
    try {
      const res = await apiFetch(`/api/resident/admin/residents/${r.user_id}/reset-record`, { method: 'POST', body: { note: 'lease renewal' } });
      toast.success(`Record reset · ${res.archived_badges} badge(s) archived`);
      load();
    } catch (e) { toast.error(e.detail || 'Reset failed'); }
  };

  if (!propertyId) return <p className="text-xs text-zinc-500" data-testid="rm-select-property">Select a property to manage {occupant.toLowerCase()}s.</p>;
  if (!data) return null;
  const openFlags = (data.flags || []).filter((f) => f.status === 'open');
  const unitKeys = Object.keys(data.units || {});

  return (
    <div className="space-y-4" data-testid="resident-management-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.2em]" style={{ color: GOLD }}>
          <Users className="h-4 w-4" /> {occupant} Management · {propertyName || data.property?.property_name}
        </h3>
        <div className="flex items-center gap-2">
          {data.active_code && (
            <button onClick={() => setShowQr(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="rm-qr-invite-btn">
              <QrCode className="h-3.5 w-3.5" /> QR Invite Poster
            </button>
          )}
          <button onClick={load} className="p-1.5 rounded-full border border-zinc-600 text-zinc-300" data-testid="rm-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Open occupancy flags */}
      {openFlags.length > 0 && (
        <div className="rounded-lg border border-red-800 bg-red-950/30 p-3 space-y-2" data-testid="rm-open-flags">
          <p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-red-400">
            <Flag className="h-3.5 w-3.5" /> Occupancy Flags — units on hold pending your confirmation
          </p>
          {openFlags.map((f) => (
            <div key={f.flag_id} className="flex items-start justify-between gap-2 rounded bg-zinc-900/60 p-2.5" data-testid={`rm-flag-${f.flag_id}`}>
              <div>
                <p className="text-xs font-bold text-white">Unit {f.unit_number}</p>
                <p className="text-[11px] text-zinc-400">{f.reason}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">Flagged by {f.flagged_by} · {new Date(f.created_at).toLocaleString()}</p>
              </div>
              <button onClick={() => resolveFlag(f.flag_id)}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold shrink-0" style={{ backgroundColor: GOLD, color: '#1a1a1a' }}
                data-testid={`rm-resolve-flag-${f.flag_id}`}>
                Confirm Updated
              </button>
            </div>
          ))}
        </div>
      )}

      {unitKeys.length === 0 && (
        <p className="text-xs text-zinc-500 rounded-lg border border-zinc-800 p-4" data-testid="rm-empty">No registered {occupant.toLowerCase()}s yet.</p>
      )}

      {unitKeys.map((unit) => {
        const residents = data.units[unit];
        const anyActive = residents.some((r) => !['deactivated'].includes(r.resident_status));
        const anyHeld = residents.some((r) => r.resident_status === 'on_hold');
        return (
          <div key={unit} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-3" data-testid={`rm-unit-${unit}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-black text-white">Unit {unit}
                <span className="ml-2 text-[10px] text-zinc-500 font-normal">{residents.filter((r) => r.is_account_holder && r.resident_status !== 'deactivated').length}/2 account holders</span>
              </p>
              {anyActive && (
                <div className="flex gap-1.5">
                  {anyHeld ? (
                    <button onClick={() => unitStatus(unit, 'active')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border border-emerald-800 text-emerald-400"
                      data-testid={`rm-unit-release-${unit}`}>
                      <PlayCircle className="h-3 w-3" /> Release Hold
                    </button>
                  ) : (
                    <button onClick={() => unitStatus(unit, 'on_hold')}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border border-amber-800 text-amber-400"
                      data-testid={`rm-unit-hold-${unit}`}>
                      <PauseCircle className="h-3 w-3" /> Hold Unit
                    </button>
                  )}
                  <button onClick={() => unitStatus(unit, 'deactivated')}
                    className="px-2 py-1 rounded-full text-[10px] font-bold border border-red-900 text-red-400"
                    data-testid={`rm-unit-deactivate-${unit}`}>
                    Deactivate Unit
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {residents.map((r) => (
                <div key={r.user_id} className="flex items-center justify-between gap-2 rounded bg-zinc-900/60 p-2.5 flex-wrap" data-testid={`rm-resident-${r.user_id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-white truncate">{r.name}
                      <span className="ml-2 text-[#FFD60A]" data-testid={`rm-account-type-${r.user_id}`}>{accountLabelFor(r, propertyType)}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${STATUS_STYLES[r.resident_status] || STATUS_STYLES.active}`}
                        data-testid={`rm-status-badge-${r.user_id}`}>
                        {(r.resident_status || 'active').replace('_', ' ')}
                      </span>
                      {r.onboarding_email_sent_at && <span className="ml-1.5 text-[9px] text-emerald-500 uppercase">Onboarded</span>}
                    </p>
                    <p className="text-[10px] text-zinc-500 truncate">{r.email} · {r.phone || '—'}
                      {(r.linked_users || []).length > 0 && ` · linked: ${r.linked_users.map((l) => l.name).join(', ')}`}
                    </p>
                  </div>
                  {r.resident_status !== 'deactivated' && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isAdmin && (
                        <button onClick={() => resetRecord(r)} title="Lease renewal — reset Operation Record"
                          className="px-2 py-1 rounded border border-zinc-600 text-[9px] font-bold uppercase text-zinc-400 hover:text-white"
                          data-testid={`rm-reset-record-${r.user_id}`}>
                          Renew Lease
                        </button>
                      )}
                      <button onClick={() => sendOnboarding(r.user_id)} title="Send onboarding email"
                        className="p-1.5 rounded border border-zinc-600 text-zinc-300 hover:text-white"
                        data-testid={`rm-onboarding-${r.user_id}`}>
                        <Mail className="h-3.5 w-3.5" />
                      </button>
                      <select value={r.resident_status || 'active'} onChange={(e) => setStatus(r.user_id, e.target.value)}
                        className="rounded bg-zinc-800 border border-zinc-600 px-1.5 py-1 text-[11px] text-white"
                        data-testid={`rm-status-select-${r.user_id}`}>
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="on_hold">On Hold</option>
                        <option value="deactivated">Deactivated</option>
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <QrInvitePoster open={showQr} onClose={() => setShowQr(false)}
        propertyName={propertyName || data.property?.property_name} code={data.active_code} />
    </div>
  );
}
